import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    useReducer,
    Dispatch,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import SETTING_KEYS from '../constants/setting-keys';
import {
    WaintingPeriodDTO,
    SpatialGradientDTO,
    SARDTO,
    ArtifactDTO,
    TempDTO,
    SlewDTO,
} from '../models/ConditionalsModels';
import { listSourceFiles } from '../services/apiService';
import { UserFile } from '../components/files/FilesOverviewPage';

interface CustomField {
    heading: string;
    value: {
        type: 'doc';
        content: any[];
    };
}

interface ICatalogNumber {
    id: number;
    unavailable?: boolean;
    value?: string;
    length?: number;
    diameter?: number;
    info?: string;
}

export interface RegionalInfo {
    [SETTING_KEYS.filename]?: string;
    [SETTING_KEYS.image]?: string;
    regions: string[];
    catalogNumbers: ICatalogNumber[];
    mriStatus: number;
    ifu: string | UserFile;
    image: string | UserFile;
    otherFiles: string[] | UserFile[];
    conditionals?: string[];
    customFields?: CustomField[];
    precaution?: string;
    sources?: ISource[];
    model?: string;
    distEndDate?: Date;
}

export interface ISource {
    title: string;
    content: string;
    documentDate: Date;
}

export const initSingleRegionalInfo: RegionalInfo = {
    regions: [],
    catalogNumbers: [
        {
            id: 0,
            value: '',
            info: '',
            diameter: null,
            length: null,
        },
    ],
    mriStatus: -1,
    ifu: null,
    image: null,
    otherFiles: [],
    conditionals: null,
    customFields: [],
    precaution: '',
    sources: [
        { title: 'IFU', content: '', documentDate: new Date() },
    ] as ISource[],
    model: '',
};

export const initRegionalInfos = [initSingleRegionalInfo];

export const initialInfo = {
    [SETTING_KEYS.manufacturer]: '',
    [SETTING_KEYS.model]: '',
    [SETTING_KEYS.category]: '',
    [SETTING_KEYS.gudidDeviceId]: '',
    [SETTING_KEYS.distEndDate]: '',
    [SETTING_KEYS.gmdnDefinition]: '',
    [SETTING_KEYS.type]: '',
    [SETTING_KEYS.bodyPlacement]: [''],
    [SETTING_KEYS.notes]: '',
    [SETTING_KEYS.customFields]: [] as CustomField[],
    [SETTING_KEYS.source]: '',
    [SETTING_KEYS.docDate]: '',
};

export const getInitSingleConditional = () => ({
    id: uuidv4(),
    [SETTING_KEYS.addInfo]: '',
    [SETTING_KEYS.artefacts]: [] as ArtifactDTO[],
    [SETTING_KEYS.brms]: '',
    [SETTING_KEYS.coilType]: '',
    [SETTING_KEYS.mriArc]: '',
    [SETTING_KEYS.fieldStrength]: [] as any[],
    [SETTING_KEYS.spatialGradient]: { gradients: [] } as SpatialGradientDTO,
    [SETTING_KEYS.slewRate]: {} as SlewDTO,
    [SETTING_KEYS.sar]: [] as SARDTO[],
    [SETTING_KEYS.temp]: [] as TempDTO[],
    [SETTING_KEYS.prep]: '',
    [SETTING_KEYS.waitingPeriod]: {} as WaintingPeriodDTO,
    [SETTING_KEYS.mriStatus]: -1,
});

export const initConditionals = [] as any[];

type UpdateAction = {
    type:
        | 'UPDATE'
        | 'RESET'
        | 'BULK_UPDATE'
        | 'ADD_CONDITIONAL'
        | 'UPDATE_CONDITIONAL'
        | 'DELETE_CONDITIONAL'
        | 'UPDATE_CONDITIONAL_FIELD';
    name?: string;
    value?: any;
    payload?: any;
    conditionalId?: string;
    conditionalIndex?: number; // Keep for backward compatibility during migration
};

type RegionalInfoAction = {
    type:
        | 'ADD_REGIONAL_INFO'
        | 'UPDATE_REGIONAL_INFO'
        | 'DELETE_REGIONAL_INFO'
        | 'UPDATE_REGIONAL_INFO_FIELD'
        | 'RESET'
        | 'BULK_UPDATE';
    payload?: any;
    regionalInfoIndex?: number;
};

type ImplantContextType = {
    file: File | null;
    setFile: Dispatch<File | null>;
    fileName: string | null;
    setFileName: Dispatch<string | null>;
    info: any;
    dispatchInfo: Dispatch<UpdateAction>;
    conditionals: any[];
    dispatchConditionals: Dispatch<UpdateAction>;
    regionalInfos: RegionalInfo[];
    dispatchRegionalInfos: Dispatch<RegionalInfoAction>;
    sourceSelected: boolean;
    setSourceSelected: Dispatch<boolean>;
    usesExistingFile: boolean;
    setUsesExistingFile: Dispatch<boolean>;
    allFiles: string[];
    setAllFiles: Dispatch<string[]>;
    runPDFExtractor: boolean;
    setRunPDFExtractor: Dispatch<boolean>;
    // Helper functions for conditionals management
    addConditional: () => void;
    updateConditional: (id: string, conditional: any) => void;
    deleteConditional: (id: string) => void;
    duplicateConditional: (id: string) => void;
    updateConditionalField: (
        conditionalId: string,
        fieldName: string,
        value: any
    ) => void;
    // Helper functions for regional info management
    addRegionalInfo: () => void;
    updateRegionalInfo: (index: number, regionalInfo: any) => void;
    deleteRegionalInfo: (index: number) => void;
    duplicateRegionalInfo: (index: number) => void;
    updateRegionalInfoField: (
        regionalInfoIndex: number,
        fieldName: string,
        value: any
    ) => void;
};

const defaultContextValue: ImplantContextType = {
    file: null,
    setFile: () => {},
    fileName: null,
    setFileName: () => {},
    info: initialInfo,
    dispatchInfo: () => {},
    conditionals: initConditionals,
    dispatchConditionals: () => {},
    regionalInfos: initRegionalInfos,
    dispatchRegionalInfos: () => {},
    sourceSelected: false,
    setSourceSelected: () => {},
    usesExistingFile: false,
    setUsesExistingFile: () => {},
    allFiles: [],
    setAllFiles: () => {},
    runPDFExtractor: true,
    setRunPDFExtractor: () => {},
    addConditional: () => {},
    updateConditional: () => {},
    deleteConditional: () => {},
    duplicateConditional: () => {},
    updateConditionalField: () => {},
    addRegionalInfo: () => {},
    updateRegionalInfo: () => {},
    deleteRegionalInfo: () => {},
    duplicateRegionalInfo: () => {},
    updateRegionalInfoField: () => {},
};

export const infoReducer = (state: any, action: UpdateAction) => {
    switch (action.type) {
        case 'UPDATE':
            const { name, value } = action.payload;
            return {
                ...state,
                [name]: value,
            };
        case 'BULK_UPDATE':
            return {
                ...action.payload,
            };
        case 'RESET':
            return initialInfo;
        default:
            return state;
    }
};

export const regionalInfoReducer = (
    state: RegionalInfo[],
    action: RegionalInfoAction
) => {
    switch (action.type) {
        case 'ADD_REGIONAL_INFO':
            return [...state, { ...initSingleRegionalInfo, ...action.payload }];

        case 'UPDATE_REGIONAL_INFO':
            const {
                regionalInfoIndex: updateIndex,
                payload: regionalInfoData,
            } = action;
            if (
                updateIndex !== undefined &&
                updateIndex >= 0 &&
                updateIndex < state.length
            ) {
                return state.map((regionalInfo: RegionalInfo, index: number) =>
                    index === updateIndex
                        ? { ...regionalInfo, ...regionalInfoData }
                        : regionalInfo
                );
            }
            return state;

        case 'DELETE_REGIONAL_INFO':
            const { regionalInfoIndex: deleteIndex } = action;
            if (
                deleteIndex !== undefined &&
                deleteIndex >= 0 &&
                deleteIndex < state.length
            ) {
                const newRegionalInfos = state.filter(
                    (_: RegionalInfo, index: number) => index !== deleteIndex
                );
                return newRegionalInfos.length === 0
                    ? initRegionalInfos
                    : newRegionalInfos;
            }
            return state;

        case 'UPDATE_REGIONAL_INFO_FIELD':
            const {
                regionalInfoIndex: fieldIndex,
                payload: { name: fieldName, value: fieldValue },
            } = action;
            if (
                fieldIndex !== undefined &&
                fieldIndex >= 0 &&
                fieldIndex < state.length
            ) {
                return state.map((regionalInfo: RegionalInfo, index: number) =>
                    index === fieldIndex
                        ? { ...regionalInfo, [fieldName]: fieldValue }
                        : regionalInfo
                );
            }
            return state;

        case 'RESET':
            return initRegionalInfos;

        case 'BULK_UPDATE':
            return Array.isArray(action.payload)
                ? action.payload
                : [action.payload];

        default:
            return state;
    }
};

export const conditionalsReducer = (state: any[], action: UpdateAction) => {
    switch (action.type) {
        case 'UPDATE':
            const { name, value } = action.payload;
            // For backward compatibility - update first conditional
            if (state.length === 0)
                return [{ ...getInitSingleConditional(), [name]: value }];
            return state.map((conditional, index) =>
                index === 0 ? { ...conditional, [name]: value } : conditional
            );

        case 'BULK_UPDATE':
            // Replace all conditionals with the provided array
            // Ensure all conditionals have an id
            const bulkUpdated = Array.isArray(action.payload)
                ? action.payload
                : [action.payload];
            return bulkUpdated.map((conditional: any) =>
                conditional._id || conditional.id
                    ? conditional
                    : {
                          ...getInitSingleConditional(),
                          ...conditional,
                          id: uuidv4(),
                      }
            );

        case 'RESET':
            return initConditionals;

        case 'ADD_CONDITIONAL':
            const newConditional = {
                ...getInitSingleConditional(),
                ...action.payload,
            };
            // Ensure id is set (use existing if provided, otherwise keep the generated one)
            if (!newConditional._id) {
                newConditional.id = uuidv4();
            }
            return [...state, newConditional];

        case 'UPDATE_CONDITIONAL':
            const {
                conditionalId: updateId,
                conditionalIndex: updateIndex,
                payload: conditionalData,
            } = action;
            if (updateId !== undefined) {
                return state.map((conditional) =>
                    conditional.id === updateId || conditional._id === updateId
                        ? { ...conditional, ...conditionalData }
                        : conditional
                );
            }
            // Backward compatibility: support index-based updates
            if (
                updateIndex !== undefined &&
                updateIndex >= 0 &&
                updateIndex < state.length
            ) {
                return state.map((conditional, index) =>
                    index === updateIndex
                        ? { ...conditional, ...conditionalData }
                        : conditional
                );
            }
            return state;

        case 'DELETE_CONDITIONAL':
            const { conditionalId: deleteId, conditionalIndex: deleteIndex } =
                action;
            if (deleteId !== undefined) {
                const newState = state.filter(
                    (conditional) =>
                        conditional.id !== deleteId &&
                        conditional._id !== deleteId
                );
                return newState.length === 0 ? initConditionals : newState;
            }
            // Backward compatibility: support index-based deletes
            if (
                deleteIndex !== undefined &&
                deleteIndex >= 0 &&
                deleteIndex < state.length
            ) {
                const newState = state.filter(
                    (_, index) => index !== deleteIndex
                );
                return newState.length === 0 ? initConditionals : newState;
            }
            return state;

        case 'UPDATE_CONDITIONAL_FIELD':
            const {
                conditionalId: fieldId,
                conditionalIndex: fieldIndex,
                name: fieldName,
                value: fieldValue,
            } = action.payload;
            if (fieldId !== undefined) {
                return state.map((conditional) =>
                    conditional.id === fieldId || conditional._id === fieldId
                        ? { ...conditional, [fieldName]: fieldValue }
                        : conditional
                );
            }
            // Backward compatibility: support index-based field updates
            if (
                fieldIndex !== undefined &&
                fieldIndex >= 0 &&
                fieldIndex < state.length
            ) {
                return state.map((conditional, index) =>
                    index === fieldIndex
                        ? { ...conditional, [fieldName]: fieldValue }
                        : conditional
                );
            }
            return state;

        default:
            return state;
    }
};

const ImplantContext = createContext<ImplantContextType>(defaultContextValue);

export const ImplantProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [conditionals, dispatchConditionals] = useReducer(
        conditionalsReducer,
        initConditionals
    );
    const [info, dispatchInfo] = useReducer(infoReducer, initialInfo);
    const [regionalInfos, dispatchRegionalInfos] = useReducer(
        regionalInfoReducer,
        initRegionalInfos
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(String);
    const [sourceSelected, setSourceSelected] = useState(false);
    const [usesExistingFile, setUsesExistingFile] = useState(false);
    const [allFiles, setAllFiles] = useState<string[]>([]);
    const [runPDFExtractor, setRunPDFExtractor] = useState(false);

    const getData = async () => {
        setIsLoading(true);
        try {
            const files = await listSourceFiles();
            setAllFiles(files);
        } catch (error) {
            setError('Error fetching data');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    // Helper functions for conditionals management
    const addConditional = () => {
        dispatchConditionals({ type: 'ADD_CONDITIONAL' });
    };

    const updateConditional = (id: string, conditional: any) => {
        dispatchConditionals({
            type: 'UPDATE_CONDITIONAL',
            conditionalId: id,
            payload: conditional,
        });
    };

    const deleteConditional = (id: string) => {
        // First, clear any regionalInfo that references this conditional
        regionalInfos.forEach((regionalInfo: any, index: number) => {
            if (
                regionalInfo.conditionals === id ||
                regionalInfo.conditionals?._id === id
            ) {
                dispatchRegionalInfos({
                    type: 'UPDATE_REGIONAL_INFO_FIELD',
                    regionalInfoIndex: index,
                    payload: { name: 'conditionals', value: null },
                });
            }
        });

        // Then delete the conditional itself
        dispatchConditionals({
            type: 'DELETE_CONDITIONAL',
            conditionalId: id,
        });
    };

    const duplicateConditional = (id: string) => {
        const conditionalToDuplicate = conditionals.find(
            (c: any) => c.id === id || c._id === id
        );
        if (conditionalToDuplicate) {
            const duplicated = JSON.parse(
                JSON.stringify(conditionalToDuplicate)
            );
            // Generate new ID for the duplicate
            duplicated.id = uuidv4();
            // Remove _id if present to avoid conflicts
            delete duplicated._id;
            dispatchConditionals({
                type: 'ADD_CONDITIONAL',
                payload: duplicated,
            });
        }
    };

    const updateConditionalField = (
        conditionalId: string,
        fieldName: string,
        value: any
    ) => {
        dispatchConditionals({
            type: 'UPDATE_CONDITIONAL_FIELD',
            payload: { conditionalId, name: fieldName, value },
        });
    };

    // Helper functions for regional info management
    const addRegionalInfo = () => {
        dispatchRegionalInfos({ type: 'ADD_REGIONAL_INFO' });
    };

    const updateRegionalInfo = (index: number, regionalInfo: any) => {
        dispatchRegionalInfos({
            type: 'UPDATE_REGIONAL_INFO',
            regionalInfoIndex: index,
            payload: regionalInfo,
        });
    };

    const deleteRegionalInfo = (index: number) => {
        dispatchRegionalInfos({
            type: 'DELETE_REGIONAL_INFO',
            regionalInfoIndex: index,
        });
    };

    const duplicateRegionalInfo = (index: number) => {
        if (index >= 0 && index < regionalInfos.length) {
            const regionalInfoToDuplicate = JSON.parse(
                JSON.stringify(regionalInfos[index])
            );
            regionalInfoToDuplicate.conditionals = null;
            for (const source of regionalInfoToDuplicate.sources) {
                source.documentDate = new Date(source.documentDate);
            }
            dispatchRegionalInfos({
                type: 'ADD_REGIONAL_INFO',
                payload: regionalInfoToDuplicate,
            });
        }
    };

    const updateRegionalInfoField = (
        regionalInfoIndex: number,
        fieldName: string,
        value: any
    ) => {
        dispatchRegionalInfos({
            type: 'UPDATE_REGIONAL_INFO_FIELD',
            regionalInfoIndex,
            payload: { name: fieldName, value },
        });
    };

    const value = {
        file,
        setFile,
        fileName,
        setFileName,
        conditionals,
        dispatchConditionals,
        info,
        dispatchInfo,
        regionalInfos,
        dispatchRegionalInfos,
        isLoading,
        setIsLoading,
        error,
        setError,
        sourceSelected,
        setSourceSelected,
        usesExistingFile,
        setUsesExistingFile,
        allFiles,
        setAllFiles,
        runPDFExtractor,
        setRunPDFExtractor,
        addConditional,
        updateConditional,
        deleteConditional,
        duplicateConditional,
        updateConditionalField,
        addRegionalInfo,
        updateRegionalInfo,
        deleteRegionalInfo,
        duplicateRegionalInfo,
        updateRegionalInfoField,
    };

    return (
        <ImplantContext.Provider value={value}>
            {children}
        </ImplantContext.Provider>
    );
};

export const useImplantContext = () => useContext(ImplantContext);
